import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import { R2_IMAGES } from '../utils/r2Utils';

const Works = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchParams, setSearchParams] = useSearchParams();

  // 页面加载时根据URL参数设置筛选器
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam && ['all', 'academic', 'game-vfx', 'ui-web'].includes(filterParam)) {
      setActiveFilter(filterParam);
    }
  }, [searchParams]);

  // 学术文章数据
  const publications = [
    {
      id: 1,
      type: 'academic',
      title: "Exploring and Modeling the Effects of Eye-Tracking Accuracy and Precision on Gaze-Based Steering in Virtual Environments",
      authors: "Xuning Hu, Yichuan Zhang, Yushi Wei, Liangyuting Zhang, Yue Li, Wolfgang Stuerzlinger, Hai-Ning Liang",
      conference: "2025 IEEE Transactions on Visualization and Computer Graphics (TVCG) - ISMAR 2025 Best Paper Nomination",
      year: "2025",
      doi: "10.1145/3706599.3720273",
      description: "This paper explores how eye-tracking accuracy and precision affect user performance in gaze-based steering tasks within virtual reality environments. Received Best Paper Nomination at ISMAR 2025.",
      link: "/academic/AcademicPage_1_TVCG",
      image: R2_IMAGES.tvcgHeader,
      tags: ["HCI", "VR", "Eye-tracking", "User Study", "Gaze-based Interaction", "Best Paper"]
    },
    {
      id: 2,
      type: 'academic',
      title: "Exploring the Feasibility of Gaze-Based Navigation Across Path Types",
      authors: "Yichuan Zhang, Liangyuting Zhang, Xuning Hu, Yong Yue, Hai-Ning Liang",
      conference: "International Symposium on Mixed and Augmented Reality (ISMAR) Gemini Workshop",
      year: "2024",
      doi: "",
      description: "This work investigates the feasibility and effectiveness of gaze-based navigation techniques across different types of paths in virtual environments.",
      link: "/academic/AcademicPage_2_ISMAR",
      image: R2_IMAGES.ismarWorkshopHeader,
      tags: ["VR", "Gaze Navigation", "ISMAR", "Workshop", "Path Analysis"]
    },
    {
      id: 3,
      type: 'academic',
      title: "Exploring and Modeling Gaze-Based Steering Behavior in Virtual Reality",
      authors: "Xuning Hu, Yichuan Zhang, Yushi Wei, Yue Li, Wolfgang Stuerzlinger, Hai-Ning Liang",
      conference: "The CHI Conference on Human Factors in Computing Systems (LBW 2025)",
      year: "2025",
      doi: "",
      description: "A comprehensive investigation into gaze-based steering behavior patterns in virtual reality environments with implications for 3D piloting interfaces.",
      link: "/academic/AcademicPage_3_CHI_LBW",
      image: R2_IMAGES.projectImage,
      tags: ["CHI", "VR", "Gaze Steering", "3D Piloting", "HCI"]
    },
    {
      id: 4,
      type: 'academic',
      title: "Large Language Model Assisted Multi-Agent Dialogue for Ontology Alignment",
      authors: "Shiyao Zhang, Yuji Dong, Yichuan Zhang, Terry R Payne, Jie Zhang",
      conference: "The 23rd International Conference on Autonomous Agents and Multiagent Systems (AAMAS 2024)",
      year: "2024",
      doi: "",
      description: "This research explores the application of large language models in facilitating multi-agent dialogue systems for automated ontology alignment tasks.",
      link: "/academic/AcademicPage_4_AAMAS",
      image: R2_IMAGES.aamasHeader,
      tags: ["LLM", "Multi-Agent", "Ontology", "AAMAS", "AI"]
    }
  ];

  // 其他作品数据
  const projects = [
    {
      id: 5,
      type: 'game',
      title: "The Intelligent USA Route Planner and Travel Assistant",
      description: "An AI-powered travel planning system that intelligently suggests routes, accommodations, and activities across the United States using machine learning algorithms and real-time data integration.",
      technologies: ["React", "Node.js", "Machine Learning", "Google Maps API", "OpenAI API"],
      year: "2024",
      category: "AI Application",
      imageUrl: R2_IMAGES.gameVFX1,
      demo: "/coming-soon",
      github: "#",
      tags: ["AI", "Travel", "Route Planning", "React", "ML"]
    },
    {
      id: 6,
      type: 'vfx',
      title: "GPU-Based Visual Effects System",
      description: "Advanced real-time visual effects system utilizing GPU compute shaders for particle simulation, fluid dynamics, and procedural animation in interactive applications.",
      technologies: ["OpenGL", "HLSL", "Compute Shaders", "Unity", "C++"],
      year: "2023",
      category: "Visual Effects",
      imageUrl: R2_IMAGES.gameVFX2,
      demo: "/coming-soon",
      artstation: "#",
      tags: ["GPU", "VFX", "Shaders", "Real-time", "Unity"]
    },
    {
      id: 7,
      type: 'game',
      title: "Gaze-Based Rhythm Game",
      description: "Innovative rhythm game that uses eye-tracking technology for gameplay interaction, combining music, visual feedback, and gaze-based controls for an immersive gaming experience.",
      technologies: ["Unity", "Eye-tracking SDK", "C#", "Audio Analysis", "VR"],
      year: "2024",
      category: "Game Development",
      imageUrl: R2_IMAGES.gameVFX5,
      demo: "/coming-soon",
      github: "#",
      tags: ["Unity", "Eye-tracking", "Rhythm Game", "VR", "Music"]
    },
    {
      id: 8,
      type: 'game',
      title: "2D & 3D Hybrid Game Collection",
      description: "A series of experimental games exploring the intersection of 2D and 3D gameplay mechanics, featuring unique art styles and innovative control schemes.",
      technologies: ["Unity", "Blender", "C#", "2D/3D Graphics", "Procedural Generation"],
      year: "2023",
      category: "Game Development",
      imageUrl: R2_IMAGES.gameVFX4,
      demo: "/coming-soon",
      github: "#",
      tags: ["Unity", "2D/3D", "Game Design", "Art", "Procedural"]
    },
    {
      id: 9,
      type: 'research',
      title: "Context-Aware Text Augmented System",
      description: "Intelligent text augmentation system that provides contextual information and suggestions in real-time, enhancing reading and writing experiences through AI-powered insights.",
      technologies: ["React", "TypeScript", "NLP", "Machine Learning", "Context API"],
      year: "2024",
      category: "Gaze & VFX",
      imageUrl: R2_IMAGES.gameVFX3,
      demo: "#",
      github: "#",
      tags: ["AI", "NLP", "Context-Aware", "React", "TypeScript"]
    },
    {
      id: 10,
      type: 'ui',
      title: "X-CHI Lab Research Website",
      description: "Modern, responsive website for the X-CHI Lab at HKUST-GZ, featuring interactive research showcases, publication galleries, and team member profiles with smooth animations.",
      technologies: ["React", "Tailwind CSS", "Framer Motion", "Three.js"],
      year: "2024",
      category: "Web Development",
      image: "🎨",
      demo: "#",
      github: "#",
      tags: ["React", "UI/UX", "Web Design", "Research", "Three.js"],
      isComingSoon: true // TODO: Remove this to restore normal card display
    },

    {
      id: 12,
      type: 'ui',
      title: "Personal Portfolio Website",
      description: "This interactive portfolio website featuring 3D clouds, smooth animations, and dynamic content presentation. Built with modern web technologies and optimized for performance.",
      technologies: ["React", "Three.js", "Framer Motion", "Tailwind CSS", "WebGL"],
      year: "2024",
      category: "Portfolio Design",
      image: "🌐",
      demo: "#",
      github: "#",
      tags: ["React", "Three.js", "Portfolio", "3D", "Animation"],
      isComingSoon: true // TODO: Remove this to restore normal card display
    },

  ];

  const allWorks = [...publications, ...projects];

  // 筛选逻辑
  const filteredWorks = allWorks.filter(work => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'academic') return work.type === 'academic';
    if (activeFilter === 'game-vfx') return ['game', 'animation', 'vfx'].includes(work.type) || work.category === 'Gaze & VFX';
    if (activeFilter === 'ui-web') return ['ui', 'mobile'].includes(work.type) && work.category !== 'Gaze & VFX';
    return work.type === activeFilter;
  });

  // 筛选按钮配置
  const filterButtons = [
    { key: 'all', label: 'All Works', count: allWorks.length },
    { key: 'academic', label: 'Academic', count: publications.length },
    { key: 'game-vfx', label: 'Game & VFX', count: projects.filter(p => ['game', 'animation', 'vfx'].includes(p.type) || p.category === 'Gaze & VFX').length },
    { key: 'ui-web', label: 'UI/UX and Web Development', count: projects.filter(p => ['ui', 'mobile'].includes(p.type) && p.category !== 'Gaze & VFX').length }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'rgb(249, 248, 241)' }}>
      <Header showNavItems={true} />
      
      {/* 主要内容区域 */}
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* 页面标题 */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 
              className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-gray-800"
              style={{ 
                fontFamily: 'Arial, sans-serif'
              }}
            >
              Works & Publications
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              A comprehensive collection of my academic research, creative projects, and technical works spanning 
              HCI/HAI research, VR/AR applications, AI-powered systems, game development, and interactive experiences.
            </p>
          </motion.div>

          {/* 筛选按钮 */}
          <motion.div 
            className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-12 px-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {filterButtons.map((button) => (
              <button
                key={button.key}
                onClick={() => {
                  setActiveFilter(button.key);
                  if (button.key === 'all') {
                    setSearchParams({});
                  } else {
                    setSearchParams({ filter: button.key });
                  }
                }}
                className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${
                  activeFilter === button.key
                    ? 'bg-gray-800 text-white shadow-lg'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span className="hidden sm:inline">{button.label}</span>
                <span className="sm:hidden">{button.label.split(' ')[0]}</span>
                {' '}({button.count})
              </button>
            ))}
          </motion.div>

          {/* 作品展示区域 */}
          <div className="space-y-8">
            {/* 当筛选为 academic 时，学术文章使用横向布局 */}
            {activeFilter === 'academic' && filteredWorks.filter(work => work.type === 'academic').length > 0 && (
              <motion.div 
                className="space-y-6"
                layout
              >
                {filteredWorks
                  .filter(work => work.type === 'academic')
                  .map((work, index) => (
                    <motion.div
                      key={work.id}
                      layout
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -30 }}
                      transition={{ 
                        duration: 0.5, 
                        delay: index * 0.1,
                        layout: { duration: 0.3 }
                      }}
                    >
                      <AcademicCard work={work} />
                    </motion.div>
                  ))
                }
              </motion.div>
            )}

            {/* 其他情况下，所有作品都使用网格布局 */}
            {activeFilter !== 'academic' && filteredWorks.length > 0 && (
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                layout
              >
                {filteredWorks.map((work, index) => (
                  <motion.div
                    key={work.id}
                    layout
                    className="h-full"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ 
                      duration: 0.5, 
                      delay: index * 0.1,
                      layout: { duration: 0.3 }
                    }}
                  >
                    {work.type === 'academic' ? (
                      <AcademicProjectCard work={work} />
                    ) : (
                      <ProjectCard work={work} />
                    )}
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>

          {/* 空状态 */}
          {filteredWorks.length === 0 && (
            <motion.div 
              className="text-center py-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-gray-500 text-lg">No works found for this category.</p>
            </motion.div>
          )}

          {/* 统计信息 */}
          <motion.div 
            className="mt-16 text-center px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-8 bg-white/80 backdrop-blur-sm rounded-lg px-6 sm:px-8 py-4 shadow-sm">
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-gray-800">{publications.length}</div>
                <div className="text-xs sm:text-sm text-gray-600">Academic Papers</div>
              </div>
              <div className="hidden sm:block w-px h-8 bg-gray-300"></div>
              <div className="block sm:hidden w-8 h-px bg-gray-300"></div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-gray-800">{projects.length}</div>
                <div className="text-xs sm:text-sm text-gray-600">Projects</div>
              </div>
              <div className="hidden sm:block w-px h-8 bg-gray-300"></div>
              <div className="block sm:hidden w-8 h-px bg-gray-300"></div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-gray-800">{allWorks.length}</div>
                <div className="text-xs sm:text-sm text-gray-600">Total Works</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

/**
 * 学术文章卡片组件 - 响应式布局
 * 移动端使用垂直布局，桌面端使用水平布局
 */
const AcademicCard = ({ work }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    {/* 使用响应式布局：移动端垂直排列，桌面端水平排列 */}
    <div className="flex flex-col lg:flex-row">
      {/* 图片区域 - 移动端全宽，桌面端固定宽度 */}
      <div className="relative w-full lg:w-80 h-48 lg:h-48 bg-gradient-to-br from-blue-50 to-indigo-100 flex-shrink-0 overflow-hidden">
        <img 
          src={work.image || "/api/placeholder/320/192"} 
          alt={work.title}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='192' viewBox='0 0 320 192'%3E%3Cdefs%3E%3ClinearGradient id='bg' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23dbeafe'/%3E%3Cstop offset='100%25' style='stop-color:%23c7d2fe'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='320' height='192' fill='url(%23bg)'/%3E%3Cg transform='translate(160,96)'%3E%3Ccircle r='20' fill='%236366f1' opacity='0.9'/%3E%3Ctext x='0' y='5' font-family='Arial' font-size='16' fill='white' text-anchor='middle'%3E📊%3C/text%3E%3C/g%3E%3Ctext x='160' y='130' font-family='Arial' font-size='11' fill='%236366f1' text-anchor='middle' font-weight='600'%3EResearch Paper%3C/text%3E%3C/svg%3E";
          }}
        />
        {/* 年份标签 */}
        <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-gray-800 shadow-sm">
          {work.year}
        </div>
      </div>

      {/* 内容区域 - 移动端和桌面端都全宽显示 */}
      <div className="flex-1 p-4 lg:p-6 flex flex-col">
        {/* 顶部：类型标签 */}
        <div className="mb-3">
          <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
            Academic Paper
          </span>
        </div>

        {/* 标题 - 移动端使用较小字体 */}
        <h3 
          className="font-bold text-lg lg:text-xl mb-3 text-gray-900 line-clamp-2 leading-tight"
          style={{ 
            fontFamily: 'Arial, sans-serif'
          }}
        >
          {work.title}
        </h3>

        {/* 作者信息 */}
        <div className="mb-3">
          <p className="text-sm text-gray-700 font-medium">{work.authors}</p>
          <p className="text-sm text-gray-500 mt-1 italic">{work.conference || work.journal}</p>
        </div>

        {/* 描述 - 占据剩余空间 */}
        <p className="text-sm text-gray-600 mb-4 line-clamp-2 lg:line-clamp-2 leading-relaxed flex-1">
          {work.description}
        </p>

        {/* 底部区域：标签和按钮 - 移动端垂直排列，桌面端水平排列 */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          {/* 标签 */}
          <div className="flex flex-wrap gap-2 order-2 lg:order-1">
            {work.tags.slice(0, 3).map((tag, idx) => (
              <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md font-medium">
                {tag}
              </span>
            ))}
            {work.tags.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-md">
                +{work.tags.length - 3}
              </span>
            )}
          </div>

          {/* 按钮组 - 移动端全宽，桌面端自适应 */}
          <div className="flex flex-col sm:flex-row gap-3 order-1 lg:order-2 lg:flex-shrink-0">
            {work.link && (
              <a
                href={work.link}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-900 text-white px-4 lg:px-6 py-2 rounded-lg font-medium text-sm transition-colors text-center"
              >
                View Paper
              </a>
            )}
            {work.doi && (
              <a
                href={`https://dl.acm.org/doi/full/${work.doi}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 text-center"
              >
                DOI
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

// 学术文章小卡片组件 - 用于网格布局
const AcademicProjectCard = ({ work }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group h-full flex flex-col">
      {/* 顶部图片区域 */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={work.image || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='192' viewBox='0 0 400 192'%3E%3Cdefs%3E%3ClinearGradient id='bg' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23dbeafe'/%3E%3Cstop offset='100%25' style='stop-color:%23c7d2fe'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='400' height='192' fill='url(%23bg)'/%3E%3Cg transform='translate(200,96)'%3E%3Ccircle r='24' fill='%236366f1' opacity='0.8'/%3E%3Ctext x='0' y='6' font-family='Arial' font-size='20' text-anchor='middle'%3E📊%3C/text%3E%3C/g%3E%3Ctext x='200' y='130' font-family='Arial' font-size='12' fill='%236366f1' text-anchor='middle' font-weight='500'%3EAcademic Paper%3C/text%3E%3C/svg%3E"} 
          alt={work.title}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='192' viewBox='0 0 400 192'%3E%3Cdefs%3E%3ClinearGradient id='bg' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23dbeafe'/%3E%3Cstop offset='100%25' style='stop-color:%23c7d2fe'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='400' height='192' fill='url(%23bg)'/%3E%3Cg transform='translate(200,96)'%3E%3Ccircle r='24' fill='%236366f1' opacity='0.8'/%3E%3Ctext x='0' y='6' font-family='Arial' font-size='20' text-anchor='middle'%3E📊%3C/text%3E%3C/g%3E%3Ctext x='200' y='130' font-family='Arial' font-size='12' fill='%236366f1' text-anchor='middle' font-weight='500'%3EAcademic Paper%3C/text%3E%3C/svg%3E";
          }}
        />
        {/* 年份标签 */}
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-gray-700">
          {work.year}
        </div>
      </div>

      {/* 内容区域 */}
      <div className="p-6 flex flex-col flex-1">
        {/* 类型标签 */}
        <div className="mb-3">
          <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
            Academic Paper
          </span>
        </div>

        {/* 标题 */}
        <h3 
          className="font-bold text-lg mb-3 text-gray-900 line-clamp-2 leading-tight"
          style={{ 
            fontFamily: 'Arial, sans-serif'
          }}
        >
          {work.title}
        </h3>

        {/* 描述 */}
        <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">
          {work.description}
        </p>

        {/* 标签 */}
        <div className="flex flex-wrap gap-2 mb-6">
          {work.tags.slice(0, 3).map((tag, idx) => (
            <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
              {tag}
            </span>
          ))}
          {work.tags.length > 3 && (
            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
              +{work.tags.length - 3}
            </span>
          )}
        </div>

        {/* 底部按钮 */}
        <div className="flex gap-3 mt-auto">
          {work.link && (
            <a
              href={work.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-gray-900 text-white text-center py-3 px-4 rounded-lg font-medium text-sm transition-colors"
            >
              View Paper
            </a>
          )}
          {work.doi && (
            <a
              href={`https://dl.acm.org/doi/full/${work.doi}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              DOI
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// 项目作品卡片组件
const ProjectCard = ({ work }) => {
  // Coming Soon 卡片样式
  // TODO: To restore normal website project cards, simply remove the `isComingSoon: true` property from the project objects above
  if (work.isComingSoon) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group relative h-full flex flex-col">
        {/* Coming Soon 背景 */}
        <div className="relative h-48 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="text-4xl mb-2">🚀</div>
            <div 
              className="text-lg font-bold text-gray-600"
              style={{ 
                fontFamily: 'Arial, sans-serif'
              }}
            >
              Coming Soon
            </div>
          </div>
          {/* 年份标签 */}
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-gray-700">
            {work.year}
          </div>
        </div>

        {/* 内容区域 */}
        <div className="p-6 flex flex-col flex-1">
          {/* 类型标签 */}
          <div className="mb-3">
            <span className="inline-block px-3 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
              {work.category} • Coming Soon
            </span>
          </div>

          {/* 标题 */}
          <h3 
            className="font-bold text-xl mb-3 text-gray-900 line-clamp-2 leading-tight"
            style={{ 
              fontFamily: 'Arial, sans-serif'
            }}
          >
            {work.title}
          </h3>

          {/* 描述 */}
          <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed italic">
            This project is currently in development. More details will be available soon!
          </p>

          {/* 技术标签 */}
          <div className="flex flex-wrap gap-2 mb-6">
            {work.technologies.slice(0, 3).map((tech, idx) => (
              <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-md opacity-70">
                {tech}
              </span>
            ))}
            <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-md">
              ...
            </span>
          </div>

          {/* 底部按钮 */}
          <div className="flex gap-3 mt-auto">
            <button
              disabled
              className="flex-1 bg-gray-300 text-gray-500 text-center py-3 px-4 rounded-lg font-medium text-sm cursor-not-allowed"
            >
              Coming Soon
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 根据项目类型生成更精美的占位图片
  const getPlaceholderImage = (type, category) => {
    const typeConfig = {
      'game': { emoji: '🎮', color: '#10b981', bgFrom: '#ecfdf5', bgTo: '#d1fae5' },
      'animation': { emoji: '🎬', color: '#f59e0b', bgFrom: '#fffbeb', bgTo: '#fef3c7' },
      'ui': { emoji: '🎨', color: '#8b5cf6', bgFrom: '#f5f3ff', bgTo: '#e9d5ff' },
      'vfx': { emoji: '✨', color: '#ef4444', bgFrom: '#fef2f2', bgTo: '#fecaca' },
      'research': { emoji: '🔬', color: '#3b82f6', bgFrom: '#eff6ff', bgTo: '#dbeafe' },
      'mobile': { emoji: '📱', color: '#06b6d4', bgFrom: '#f0fdfa', bgTo: '#ccfbf1' }
    };
    
    const config = typeConfig[type] || { emoji: '💼', color: '#6b7280', bgFrom: '#f9fafb', bgTo: '#f3f4f6' };
    
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='192' viewBox='0 0 400 192'%3E%3Cdefs%3E%3ClinearGradient id='bg' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:${config.bgFrom}'/%3E%3Cstop offset='100%25' style='stop-color:${config.bgTo}'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='400' height='192' fill='url(%23bg)'/%3E%3Cg transform='translate(200,96)'%3E%3Ccircle r='24' fill='${config.color}' opacity='0.8'/%3E%3Ctext x='0' y='6' font-family='Arial' font-size='20' text-anchor='middle'%3E${config.emoji}%3C/text%3E%3C/g%3E%3Ctext x='200' y='130' font-family='Arial' font-size='12' fill='${config.color}' text-anchor='middle' font-weight='500'%3E${category}%3C/text%3E%3C/svg%3E`;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group h-full flex flex-col">
      {/* 顶部图片区域 */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={work.imageUrl || work.image || getPlaceholderImage(work.type, work.category)} 
          alt={work.title}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = getPlaceholderImage(work.type, work.category);
          }}
        />
        {/* 年份标签 */}
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-gray-700">
          {work.year}
        </div>
      </div>

      {/* 内容区域 */}
      <div className="p-6 flex flex-col flex-1">
        {/* 类型标签 */}
        <div className="mb-3">
          <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
            {work.category}
          </span>
        </div>

        {/* 标题 */}
        <h3 
          className="font-bold text-xl mb-3 text-gray-900 line-clamp-2 leading-tight"
          style={{ 
            fontFamily: 'Arial, sans-serif'
          }}
        >
          {work.title}
        </h3>

        {/* 描述 */}
        <p className="text-sm text-gray-700 mb-4 line-clamp-2 leading-relaxed">
          {work.description}
        </p>

        {/* 技术标签 */}
        <div className="flex flex-wrap gap-2 mb-6">
          {work.technologies.slice(0, 4).map((tech, idx) => (
            <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
              {tech}
            </span>
          ))}
          {work.technologies.length > 4 && (
            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
              +{work.technologies.length - 4}
            </span>
          )}
        </div>

        {/* 底部按钮 */}
        <div className="flex gap-3 mt-auto">
          {work.demo && (
            <a
              href={work.demo}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-gray-900 text-white text-center py-3 px-4 rounded-lg font-medium text-sm transition-colors hover:bg-gray-800"
            >
              View Demo
            </a>
          )}
          {work.github && (
            <a
              href={work.github}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              GitHub
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default Works;
